import React, { Component } from "react";
import Modal from "react-bootstrap/Modal";
import EmployeeMergeService from "../../../services/EmployeeMergeService";
import EmployeeService from "../../../services/EmployeeService";
import OrganizationService from "../../../services/OrganizationService";
import WithRoutify from "../../../hoc/WithRoutify";
import WithRouter from "../../../hoc/WithRouter";
import MergePatientSelector from "./components/MergePatientSelector";
import ConflictResolutionPanel from "./components/ConflictResolutionPanel";
import MergeSummaryPanel from "./components/MergeSummaryPanel";
import MergeProgressIndicator from "./components/MergeProgressIndicator";
import MergeReportViewer from "./components/MergeReportViewer";
import MergeHistoryViewer from "./components/MergeHistoryViewer";
import Alert from "../../../components/UI/ConfirmBox/Alert";
import dayjs from "dayjs";

const MERGE_STEPS = {
  SELECT_PATIENTS: 'select_patients',
  DETECT_CONFLICTS: 'detect_conflicts',
  RESOLVE_CONFLICTS: 'resolve_conflicts',
  REVIEW_SUMMARY: 'review_summary',
  EXECUTING: 'executing',
  COMPLETED: 'completed',
};

class MergeChartsContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: MERGE_STEPS.SELECT_PATIENTS,
      primaryPatient: null,
      secondaryPatient: null,
      mergePreview: null,
      conflicts: null,
      conflictResolutions: {},
      visitConflictResolutions: {},
      mergeResult: null,
      isLoading: false,
      error: null,
      mergeReason: '',
      mergeConfirmChecked: false,
      showMergeHistory: false,
      mergeHistoryEmployeeId: null,
      showExecuteConfirm: false,
    };
  }

  openExecuteConfirm = () => {
    if (this.state.currentStep !== MERGE_STEPS.REVIEW_SUMMARY) return;
    if (!this.state.mergeConfirmChecked) return;
    this.setState({ showExecuteConfirm: true });
  };

  closeExecuteConfirm = () => {
    this.setState({ showExecuteConfirm: false });
  };

  confirmExecuteMerge = async () => {
    this.closeExecuteConfirm();
    await this.handleExecuteMerge();
  };

  handlePrimaryPatientSelect = (patient) => {
    console.log('Primary patient selected:', patient);
    if (patient && typeof patient.employee_id !== 'string') {
      console.error('Primary patient employee_id is not a string:', patient.employee_id, typeof patient.employee_id);
    }
    this.setState({ primaryPatient: patient, error: null });
  };

  handleSecondaryPatientSelect = (patient) => {
    console.log('Secondary patient selected:', patient);
    if (patient && typeof patient.employee_id !== 'string') {
      console.error('Secondary patient employee_id is not a string:', patient.employee_id, typeof patient.employee_id);
    }
    this.setState({ secondaryPatient: patient, error: null });
  };

  handleContinueToConflicts = async () => {
    const { primaryPatient, secondaryPatient } = this.state;
    const { Loader, Toast } = this.props;

    if (!primaryPatient || !secondaryPatient) {
      this.setState({ error: 'Please select both primary and secondary patients' });
      return;
    }

    if (primaryPatient.employee_id === secondaryPatient.employee_id) {
      this.setState({ error: 'Cannot merge patient with itself' });
      return;
    }

    Loader(true);
    this.setState({ isLoading: true, error: null });

    try {
      console.log('[MergeCharts] Starting merge preview', {
        primary_employee_id: primaryPatient.employee_id,
        secondary_employee_id: secondaryPatient.employee_id,
      });
      // Get merge preview
      const preview = await EmployeeMergeService.getMergePreview(
        primaryPatient.employee_id,
        secondaryPatient.employee_id
      );

      // Handle API-level validation errors (e.g. "Secondary employee has already been merged")
      if (!preview || (preview.code && preview.code !== 200)) {
        const apiMessage =
          preview?.data?.errors?.[0] ||
          preview?.data?.error ||
          preview?.message ||
          'Failed to get merge preview';
        console.warn('[MergeCharts] Merge preview failed', {
          primary_employee_id: primaryPatient.employee_id,
          secondary_employee_id: secondaryPatient.employee_id,
          response: preview,
          message: apiMessage,
        });
        throw new Error(apiMessage);
      }

      console.log('[MergeCharts] Merge preview success', {
        primary_employee_id: primaryPatient.employee_id,
        secondary_employee_id: secondaryPatient.employee_id,
        preview,
      });

      // Detect conflicts
      const conflicts = await EmployeeMergeService.detectConflicts(
        primaryPatient.employee_id,
        secondaryPatient.employee_id
      );
      console.log('[MergeCharts] Merge conflicts detected', {
        primary_employee_id: primaryPatient.employee_id,
        secondary_employee_id: secondaryPatient.employee_id,
        conflicts,
      });

      const defaultResolutions = {};
      if (conflicts?.employee_record_conflicts?.length) {
        conflicts.employee_record_conflicts.forEach((c) => {
          defaultResolutions[c.field] = 'primary';
        });
      }
      this.setState({
        mergePreview: preview,
        conflicts: conflicts,
        conflictResolutions: defaultResolutions,
        visitConflictResolutions: {},
        currentStep: MERGE_STEPS.RESOLVE_CONFLICTS,
        isLoading: false,
      });
    } catch (error) {
      console.error('[MergeCharts] Error during preview/conflict detection:', error);
      this.setState({
        error: error.message || 'Failed to detect conflicts',
        isLoading: false,
      });
      Toast.error(error.message || 'Failed to detect conflicts');
    } finally {
      Loader(false);
    }
  };

  handleConflictResolved = (field, resolution) => {
    const { conflictResolutions } = this.state;
    const newResolutions = { ...conflictResolutions, [field]: resolution };
    this.setState({ conflictResolutions: newResolutions });
  };

  handleBulkConflictResolve = (resolutions) => {
    this.setState({ conflictResolutions: resolutions });
  };

  handleVisitConflictResolved = (visitId, resolution) => {
    this.setState((prev) => ({
      visitConflictResolutions: { ...prev.visitConflictResolutions, [visitId]: resolution },
    }));
  };

  handleContinueToSummary = () => {
    this.setState({ currentStep: MERGE_STEPS.REVIEW_SUMMARY });
  };

  handleExecuteMerge = async () => {
    const { primaryPatient, secondaryPatient, conflictResolutions, visitConflictResolutions, mergeReason } = this.state;
    const { Loader, Toast } = this.props;

    // Validate required data
    if (!primaryPatient || !primaryPatient.employee_id) {
      Toast.error('Primary patient is required');
      return;
    }

    if (!secondaryPatient || !secondaryPatient.employee_id) {
      Toast.error('Secondary patient is required');
      return;
    }

    Loader(true);
    this.setState({
      currentStep: MERGE_STEPS.EXECUTING,
      isLoading: true,
      error: null,
    });

    try {
      // Validate visit date conflict resolutions (avoid backend 500s)
      const normalizeDate = (d) => {
        if (!d) return null;
        const parsed = dayjs(String(d).trim(), ["MM/DD/YYYY", "YYYY-MM-DD"], true);
        return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(d).trim();
      };

      const visitRes = visitConflictResolutions && typeof visitConflictResolutions === "object"
        ? visitConflictResolutions
        : {};
      const changeDates = Object.entries(visitRes)
        .filter(([, r]) => r && r.action === "change_date" && r.new_date)
        .map(([visitId, r]) => ({
          visitId,
          newDate: normalizeDate(r.new_date),
        }))
        .filter((x) => x.newDate);

      // Duplicate new_date across multiple visits is not allowed
      const dateToVisits = new Map();
      changeDates.forEach(({ visitId, newDate }) => {
        const key = newDate;
        const arr = dateToVisits.get(key) || [];
        arr.push(visitId);
        dateToVisits.set(key, arr);
      });
      const duplicateDates = Array.from(dateToVisits.entries())
        .filter(([, visits]) => visits.length > 1)
        .map(([date]) => date);

      if (duplicateDates.length) {
        const msg = `Duplicate change dates selected: ${duplicateDates.join(", ")}. Please choose unique dates for each visit (or delete the extra visits).`;
        this.setState({ error: msg, currentStep: MERGE_STEPS.RESOLVE_CONFLICTS, isLoading: false });
        Toast.error(msg);
        Loader(false);
        return;
      }

      // Also block selecting a primary visit date as a target date
      const primaryDates = (this.state.conflicts?.primary_visit_dates || [])
        .map(normalizeDate)
        .filter(Boolean);
      const invalidTargets = changeDates
        .filter(({ newDate }) => primaryDates.includes(newDate))
        .map(({ newDate }) => newDate);

      if (invalidTargets.length) {
        const unique = Array.from(new Set(invalidTargets));
        const msg = `Selected date(s) conflict with existing primary visits: ${unique.join(", ")}. Please choose different dates or delete those visits.`;
        this.setState({ error: msg, currentStep: MERGE_STEPS.RESOLVE_CONFLICTS, isLoading: false });
        Toast.error(msg);
        Loader(false);
        return;
      }

      // Ensure conflictResolutions is an object, not a string
      const resolutions = typeof conflictResolutions === 'object' && conflictResolutions !== null 
        ? conflictResolutions 
        : {};

      // Extract employee IDs - ensure they are strings
      // Handle case where employee_id might be nested or an object
      let primaryEmployeeId = primaryPatient.employee_id;
      if (typeof primaryEmployeeId !== 'string') {
        if (primaryEmployeeId && typeof primaryEmployeeId === 'object') {
          console.error('Primary employee_id is an object:', primaryEmployeeId);
          // Try to extract from nested structure if needed
          primaryEmployeeId = primaryEmployeeId.id || primaryEmployeeId.employee_id || String(primaryEmployeeId);
        } else {
          primaryEmployeeId = String(primaryEmployeeId || '');
        }
      }
      
      let secondaryEmployeeId = secondaryPatient.employee_id;
      if (typeof secondaryEmployeeId !== 'string') {
        if (secondaryEmployeeId && typeof secondaryEmployeeId === 'object') {
          console.error('Secondary employee_id is an object:', secondaryEmployeeId);
          // Try to extract from nested structure if needed
          secondaryEmployeeId = secondaryEmployeeId.id || secondaryEmployeeId.employee_id || String(secondaryEmployeeId);
        } else {
          secondaryEmployeeId = String(secondaryEmployeeId || '');
        }
      }

      // Validate IDs are strings
      if (typeof primaryEmployeeId !== 'string' || primaryEmployeeId.length === 0) {
        Toast.error('Invalid primary employee ID');
        Loader(false);
        return;
      }

      if (typeof secondaryEmployeeId !== 'string' || secondaryEmployeeId.length === 0) {
        Toast.error('Invalid secondary employee ID');
        Loader(false);
        return;
      }

      console.log('[MergeCharts] Executing merge with:', {
        primaryPatient: primaryPatient,
        secondaryPatient: secondaryPatient,
        primaryEmployeeId: primaryEmployeeId,
        secondaryEmployeeId: secondaryEmployeeId,
        primaryEmployeeIdType: typeof primaryEmployeeId,
        secondaryEmployeeIdType: typeof secondaryEmployeeId,
        conflictResolutions: resolutions,
        visitConflictResolutions: visitConflictResolutions,
        mergeReason: mergeReason
      });

      // Double-check the IDs before calling
      if (!primaryEmployeeId || typeof primaryEmployeeId !== 'string') {
        console.error('Invalid primaryEmployeeId before service call:', primaryEmployeeId);
        Toast.error('Invalid primary employee ID');
        Loader(false);
        return;
      }

      if (!secondaryEmployeeId || typeof secondaryEmployeeId !== 'string') {
        console.error('Invalid secondaryEmployeeId before service call:', secondaryEmployeeId);
        Toast.error('Invalid secondary employee ID');
        Loader(false);
        return;
      }

      console.log('Calling EmployeeMergeService.executeMerge with:', {
        arg1: primaryEmployeeId,
        arg2: secondaryEmployeeId,
        arg3: resolutions,
        arg4: mergeReason
      });

      // Verify organization ID is set correctly before calling the service
      const orgId = OrganizationService.getOrgId();
      if (!orgId) {
        // Try to get orgId from patient data
        const orgIdFromPatient = primaryPatient?.organization_id || secondaryPatient?.organization_id;
        if (orgIdFromPatient) {
          console.warn('⚠️ Organization ID not in localStorage, using from patient data:', orgIdFromPatient);
          OrganizationService.storeOrgId(orgIdFromPatient);
        } else {
          Toast.error('Organization ID is not set. Please refresh the page.');
          Loader(false);
          return;
        }
      }

      const result = await EmployeeMergeService.executeMerge(
        null,
        primaryEmployeeId,
        secondaryEmployeeId,
        resolutions,
        mergeReason,
        visitConflictResolutions
      );

      // If backend returned an error envelope, surface its message
      if (result && result.code && result.code !== 200) {
        const apiMessage =
          result.data?.errors?.[0] ||
          result.data?.error ||
          result.message ||
          'Merge failed';
        console.warn('[MergeCharts] Merge execute returned non-200 code', {
          primaryEmployeeId,
          secondaryEmployeeId,
          result,
          message: apiMessage,
        });
        throw new Error(apiMessage);
      }

      if (result && result.status === 'completed') {
        console.log('[MergeCharts] Merge completed successfully', {
          primaryEmployeeId,
          secondaryEmployeeId,
          result,
        });
        this.setState({
          mergeResult: result,
          currentStep: MERGE_STEPS.COMPLETED,
          isLoading: false,
        });
        Toast.success('Charts merged successfully!');
        
        // Refresh employee list if callback provided
        if (this.props.onMergeComplete) {
          this.props.onMergeComplete();
        }
      } else {
        console.warn('[MergeCharts] Merge result not completed', {
          primaryEmployeeId,
          secondaryEmployeeId,
          result,
        });
        throw new Error(result?.error || 'Merge failed');
      }
    } catch (error) {
      console.error('[MergeCharts] Error executing merge:', error);
      this.setState({
        error: error.message || 'Failed to execute merge',
        isLoading: false,
        // If execution fails, take the user back to conflict resolution so they can fix it.
        currentStep: MERGE_STEPS.RESOLVE_CONFLICTS,
      });
      Toast.error(error.message || 'Failed to execute merge');
    } finally {
      Loader(false);
    }
  };

  handleClose = () => {
    if (this.state.currentStep === MERGE_STEPS.EXECUTING) {
      // Don't allow closing during merge
      return;
    }

    this.props.modalToogleHandler();
  };

  handleReset = () => {
    this.setState({
      currentStep: MERGE_STEPS.SELECT_PATIENTS,
      primaryPatient: null,
      secondaryPatient: null,
      mergePreview: null,
      conflicts: null,
      conflictResolutions: {},
      visitConflictResolutions: {},
      mergeResult: null,
      isLoading: false,
      error: null,
      mergeReason: '',
      mergeConfirmChecked: false,
    });
  };

  handleViewMergedPatient = () => {
    const { primaryPatient } = this.state;
    if (primaryPatient) {
      this.props.modalToogleHandler();
      // Navigate to patient detail view
      if (this.props.navigate) {
        this.props.navigate(`/employee/visit-history/${primaryPatient.employee_id}/${encodeURIComponent(`${primaryPatient.first_name} ${primaryPatient.last_name}`)}`);
      }
    }
  };

  handleViewMergeHistory = (employeeId) => {
    this.setState({
      showMergeHistory: true,
      mergeHistoryEmployeeId: employeeId || this.state.primaryPatient?.employee_id,
    });
  };

  handleCloseMergeHistory = () => {
    this.setState({
      showMergeHistory: false,
      mergeHistoryEmployeeId: null,
    });
  };

  handleRollbackComplete = () => {
    // Refresh employee list if callback provided
    if (this.props.onMergeComplete) {
      this.props.onMergeComplete();
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState?.currentStep !== this.state?.currentStep) {
      const stepLabelMap = {
        [MERGE_STEPS.SELECT_PATIENTS]: "Select Patients",
        [MERGE_STEPS.DETECT_CONFLICTS]: "Detect Conflicts",
        [MERGE_STEPS.RESOLVE_CONFLICTS]: "Resolve Conflicts",
        [MERGE_STEPS.REVIEW_SUMMARY]: "Review Summary",
        [MERGE_STEPS.EXECUTING]: "Executing",
        [MERGE_STEPS.COMPLETED]: "Completed",
      };

      const from = prevState?.currentStep;
      const to = this.state?.currentStep;
      console.log(
        `[MergeCharts] Step changed: ${stepLabelMap[from] || from || ""} -> ${stepLabelMap[to] || to || ""}`
      );
    }
  }

  render() {
    const {
      currentStep,
      primaryPatient,
      secondaryPatient,
      mergePreview,
      conflicts,
      conflictResolutions,
      visitConflictResolutions,
      mergeResult,
      isLoading,
      error,
      mergeReason,
    } = this.state;

    const { modalToogleHandler } = this.props;

    return (
      <>
        {this.state.showExecuteConfirm && (
          <Alert
            type="warning"
            custonTitle="Confirm Merge"
            contentTitle="Are you sure you want to execute this merge? This will change patient data and transfer visits."
            yesButtonName="Yes, Execute Merge"
            noButtonName="Cancel"
            confirmButtonClassName="btn btn-danger"
            onClose={this.closeExecuteConfirm}
            onDelete={this.confirmExecuteMerge}
          />
        )}

        <Modal
          show={true}
          onHide={this.handleClose}
          size="xl"
          dialogClassName="hResponsive modal-dialog-centered modal-xl"
          contentClassName="h-100 merge-refined"
          backdrop="static"
          keyboard={false}
        >
        <div className="sh_modalBox merge-wizard-box merge-refined" style={{ backgroundColor: "#fff" }}>
            <Modal.Header closeButton className="merge-refined-header border-0 pb-0">
              <Modal.Title>
                <h5 className="mb-0 merge-refined-title">Merge Patient Charts</h5>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="merge-refined-body pt-0 overflow-auto flex-grow-1 position-relative" style={{ background: "#fff" }}>
              {error &&
                currentStep !== MERGE_STEPS.SELECT_PATIENTS &&
                currentStep !== MERGE_STEPS.REVIEW_SUMMARY && (
                  <div className="alert alert-danger merge-refined-alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}

              {/* Step 1: Select Patients + Review & resolve conflicts (same page per HTML UI) */}
              {(currentStep === MERGE_STEPS.SELECT_PATIENTS || currentStep === MERGE_STEPS.RESOLVE_CONFLICTS) && (
                <div className="merge-wizard-panel active" data-merge-panel="1">
                  <div className="merge-refined-spacer">
                    <div className="row g-3">
                      <div className="col-lg-6">
                        <MergePatientSelector
                          type="primary"
                          onSelect={this.handlePrimaryPatientSelect}
                          selectedPatient={primaryPatient}
                          excludeEmployeeId={secondaryPatient?.employee_id}
                          disabled={currentStep !== MERGE_STEPS.SELECT_PATIENTS}
                        />
                      </div>
                      <div className="col-lg-6">
                        <MergePatientSelector
                          type="secondary"
                          onSelect={this.handleSecondaryPatientSelect}
                          selectedPatient={secondaryPatient}
                          excludeEmployeeId={primaryPatient?.employee_id}
                          disabled={currentStep !== MERGE_STEPS.SELECT_PATIENTS}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    id="mergeSamePatientAlert"
                    className={`alert alert-danger merge-refined-alert-danger mt-3 ${primaryPatient && secondaryPatient && primaryPatient.employee_id === secondaryPatient.employee_id ? '' : 'd-none'}`}
                    role="alert"
                  >
                    The same patient cannot be primary and secondary. Choose a different record.
                  </div>
                  {/* Review & resolve conflicts - always on same page per HTML (card always visible) */}
                  <div className="merge-resolve-section pt-3 border-top">
                    {primaryPatient && secondaryPatient && primaryPatient.employee_id !== secondaryPatient.employee_id && currentStep === MERGE_STEPS.RESOLVE_CONFLICTS && conflicts !== null ? (
                      <ConflictResolutionPanel
                        conflicts={conflicts}
                        conflictResolutions={conflictResolutions}
                        visitConflictResolutions={visitConflictResolutions}
                        onVisitConflictResolved={this.handleVisitConflictResolved}
                        primaryPatient={primaryPatient}
                        secondaryPatient={secondaryPatient}
                        mergePreview={mergePreview}
                        onConflictResolved={this.handleConflictResolved}
                        onBulkResolve={this.handleBulkConflictResolve}
                      />
                    ) : (
                      <div className="card border-0">
                        <div className="card-header sh_accordionheader">
                          <h6 className="mb-0">Review &amp; resolve conflicts</h6>
                        </div>
                        <div className="card-body p-2">
                          <p className="mb-0 small text-muted">
                            {primaryPatient && secondaryPatient && primaryPatient.employee_id !== secondaryPatient.employee_id
                              ? <>Click <strong>Next: Confirm Merge</strong> below to detect and review conflicts between these patients.</>
                              : 'Select primary and secondary patients above to review conflicts.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Review Summary (Confirm & Merge) - matches HTML data-merge-panel="2" */}
              {currentStep === MERGE_STEPS.REVIEW_SUMMARY && (
                <div className="merge-wizard-panel active" data-merge-panel="2">
                  <MergeSummaryPanel
                    mergePreview={mergePreview}
                    conflictResolutions={conflictResolutions}
                    mergeReason={mergeReason}
                    onMergeReasonChange={(reason) => this.setState({ mergeReason: reason })}
                    confirmChecked={this.state.mergeConfirmChecked}
                    onConfirmCheckedChange={(checked) => this.setState({ mergeConfirmChecked: checked })}
                  />
                </div>
              )}

              {/* Step 4: Executing Merge */}
              {currentStep === MERGE_STEPS.EXECUTING && (
                <MergeProgressIndicator
                  isVisible={true}
                  currentStep="Executing Merge"
                  progress={50}
                />
              )}

              {/* Step 5: Merge Completed */}
              {currentStep === MERGE_STEPS.COMPLETED && (
                <MergeReportViewer
                  mergeReport={mergeResult}
                  onViewPatient={this.handleViewMergedPatient}
                  onClose={this.handleClose}
                  onNewMerge={this.handleReset}
                  onViewHistory={this.handleViewMergeHistory}
                />
              )}
            </Modal.Body>

            {currentStep !== MERGE_STEPS.EXECUTING && currentStep !== MERGE_STEPS.COMPLETED && (
              <Modal.Footer className="merge-refined-footer border-top">
                <div>
                  {currentStep !== MERGE_STEPS.SELECT_PATIENTS && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        if (currentStep === MERGE_STEPS.RESOLVE_CONFLICTS) {
                          this.setState({ currentStep: MERGE_STEPS.SELECT_PATIENTS });
                        } else if (currentStep === MERGE_STEPS.REVIEW_SUMMARY) {
                          this.setState({ currentStep: MERGE_STEPS.RESOLVE_CONFLICTS });
                        }
                      }}
                    >
                      Back
                    </button>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={this.handleClose}
                  >
                    Cancel
                  </button>
                  {currentStep === MERGE_STEPS.REVIEW_SUMMARY ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      id="mergeConfirmBtn"
                      onClick={this.openExecuteConfirm}
                      disabled={!this.state.mergeConfirmChecked}
                    >
                      Confirm & Execute Merge
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={currentStep === MERGE_STEPS.SELECT_PATIENTS ? this.handleContinueToConflicts : this.handleContinueToSummary}
                      disabled={
                        currentStep === MERGE_STEPS.SELECT_PATIENTS
                          ? !primaryPatient || !secondaryPatient || isLoading
                          : (() => {
                              const empUnresolved = conflicts?.employee_record_conflicts?.length > 0 && !conflicts.employee_record_conflicts.every((c) => conflictResolutions?.[c.field]);
                              const visitDateConflicts = conflicts?.visit_date_conflicts || [];
                              const visitUnresolved = visitDateConflicts.some((c) => {
                                const r = visitConflictResolutions?.[c.visit_id];
                                return !r || !r.action || (r.action === 'change_date' && !r.new_date);
                              });
                              return empUnresolved || visitUnresolved;
                            })()
                      }
                    >
                      Next: Confirm Merge →
                    </button>
                  )}
                </div>
              </Modal.Footer>
            )}
        </div>

        {/* Merge History Modal */}
        <Modal
          show={this.state.showMergeHistory}
          onHide={this.handleCloseMergeHistory}
          size="lg"
          dialogClassName="hResponsive modal-dialog-centered"
          contentClassName="merge-refined"
        >
          <div className="sh_modalBox merge-history-modal merge-refined">
            <Modal.Header closeButton className="merge-refined-header border-0 pb-0">
              <Modal.Title>
                <h5 className="mb-0 merge-refined-title">Merge History</h5>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="merge-refined-body merge-history-body pt-0">
              <MergeHistoryViewer
                employeeId={this.state.mergeHistoryEmployeeId}
                onClose={this.handleCloseMergeHistory}
                onRollbackComplete={this.handleRollbackComplete}
              />
            </Modal.Body>
            <Modal.Footer className="merge-refined-footer border-top py-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={this.handleCloseMergeHistory}
              >
                Close
              </button>
            </Modal.Footer>
          </div>
        </Modal>
      </Modal>
      </>
    );
  }
}

export default WithRouter(WithRoutify(MergeChartsContainer));

